import Cookies from 'js-cookie';
import { v4 as uuidv4 } from 'uuid';
import { getCookieDomain } from './util/cookieDomain';

const AJS_ANONYMOUS_ID_COOKIE = 'ajs_anonymous_id';
const COOKIE_EXPIRY_DAYS = 365;

/**
 * Returns a persistent anonymous ID for GrowthBook experiment bucketing.
 *
 * Reads from the ajs_anonymous_id cookie if it exists (set by Segment post-consent,
 * or by us pre-consent). If no cookie exists, generates a new UUID and writes it
 * immediately. This runs before consent because experiment bucketing is required
 * for product display functionality.
 */
export function getOrCreateAnonymousId(): string {
  const existing = readAnonymousIdCookie();
  if (existing) {
    return existing;
  }

  const newId = uuidv4();
  writeAnonymousIdCookie(newId);
  return newId;
}

/**
 * Reads the ajs_anonymous_id cookie, stripping the surrounding double
 * quotes that both we and Segment use for the cookie value.
 */
function readAnonymousIdCookie(): string | undefined {
  const raw = Cookies.get(AJS_ANONYMOUS_ID_COOKIE);
  if (!raw) {
    return undefined;
  }
  if (raw.startsWith('"') && raw.endsWith('"')) {
    return raw.slice(1, -1);
  }
  return raw;
}

/**
 * Write the anonymous ID in Segment's expected format so analytics.js
 * recognises it during initialize() and doesn't generate a new one.
 * Cookie value is URL-encoded quoted UUID (%22uuid%22). We write via
 * document.cookie directly because js-cookie double-encodes the quotes.
 */
function writeAnonymousIdCookie(id: string): void {
  const value = encodeURIComponent('"' + id + '"');
  const domain = getCookieDomain();
  const MS_PER_DAY = 86_400_000;
  const expires = new Date(Date.now() + COOKIE_EXPIRY_DAYS * MS_PER_DAY).toUTCString();
  document.cookie =
    AJS_ANONYMOUS_ID_COOKIE +
    '=' +
    value +
    '; path=/' +
    '; expires=' +
    expires +
    '; SameSite=Lax' +
    (domain ? '; domain=' + domain : '');
}

/**
 * Clears the anonymous ID cookie. Called on logout or when the server
 * instructs a reset (shouldResetAnonymousId).
 */
export function clearAnonymousIdCookie(): void {
  Cookies.remove(AJS_ANONYMOUS_ID_COOKIE, {
    domain: getCookieDomain(),
    path: '/',
  });
}
